import type {Editor, ProjectData} from 'grapesjs';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';
import {getURISegment} from '@Utils/url.util';
import {isUserLoggedIn} from '@Libraries/user.library';
import {GA4EventParamName} from '@Libraries/ga-events';
import {getEmailMarketingCampaignById} from '@Libraries/email-campaigns-library';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import type {EmailMakerImmutableStore} from '@EmailMakerReact/email-maker-react.types';
import {EmailMakerMode} from '@EmailMakerReact/email-maker-react.types';
import type {SaveActionType} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import type {EmailMakerSavePayloadSources} from '@EmailMakerReact/lib/email-maker-save-payload-library';
import {
  buildTemplateContentForSave,
  doesEmailContainGif,
  doesEmailContainPremiumContent,
  getDesignIds,
  getImageAssetHashedNames,
  getMediaQueries,
  getTemplateName,
  getVideoAssetHashedNames,
} from '@EmailMakerReact/lib/email-maker-save-payload-library';

/** Save/load transport and storage session state for the React email maker. */

const STORAGE_STATE_MODEL_KEY = 'pmwEmailMakerStorageState';

const SAVE_EMAIL_TEMPLATE_ENDPOINT = 'emailmaker/asyncSaveEmailTemplate';
const LOAD_EMAIL_TEMPLATE_ENDPOINT = 'emailmaker/asyncLoadEmailTemplate';
const SAVE_EMAIL_TEMPLATE_TO_DB_ENDPOINT = 'emailmaker/saveEmailTemplateToDb';
const GENERATE_EMAIL_TEMPLATE_PREVIEW_ENDPOINT = 'emailmaker/generateEmailTemplatePreview';
const SAVE_COLOR_PALETTE_USAGE_ENDPOINT = 'autobrandingemailmaker/asyncSaveUserColorPaletteAndUsage';

/** URI segment 3 in-house-designer flag, forwarded verbatim on save (the backend currently ignores it). */
const IN_HOUSE_DESIGNER_URI_SEGMENT = 3;

const GA_EVENT_DESIGN_CREATED = 'EmailMaker_DesignCreated';
const GA_EVENT_DESIGN_SAVED = 'EmailMaker_DesignSaved';
const GA_SOURCE_BLANK_CANVAS = 'BlankCanvas';
const GA_SOURCE_TEMPLATE = 'Template';

/** Error codes returned by Emailmaker::asyncSaveEmailTemplate via loadJSONErrorResponse. */
export enum EmailMakerSaveErrorCode {
  AUTHENTICATE_USER = 'authenticate-user',
  CONTENT_LIMIT = 'content-limit',
  UPPERCASE_LIMIT = 'uppercase-limit',
  EXISTING_SAVE_DATA = 'existing-save-data',
  INVALID_CAMPAIGN_URLS = 'invalid-campaign-urls',
  TEMPLATE_NOT_FOUND = 'template-not-found-error',
  CAMPAIGN_OWNERSHIP = 'campaign-ownership-error',
}

export class EmailMakerSaveError extends Error {
  constructor(
    public readonly code: EmailMakerSaveErrorCode | null,
    message: string,
    /** Raw offending href strings, present only for INVALID_CAMPAIGN_URLS when the backend was gated by isReactEmailMaker. */
    public readonly invalidUrls?: string[]
  ) {
    super(message);
    this.name = 'EmailMakerSaveError';
  }
}

/** Mirrors the `existing-save-data` payload built in Emailmaker::asyncSaveEmailTemplate. */
export interface ConcurrentEditPayload {
  lastModified: string | null;
  currentUserPictureURL: string;
  otherUserPictureURL: string;
  otherUserName: string;
}

export class EmailMakerConcurrentEditError extends EmailMakerSaveError {
  constructor(public readonly payload: ConcurrentEditPayload) {
    super(EmailMakerSaveErrorCode.EXISTING_SAVE_DATA, 'Email template was modified by someone else.');
    this.name = 'EmailMakerConcurrentEditError';
  }
}

export class EmailMakerLoadError extends Error {
  constructor(
    public readonly idEmailTemplate: string,
    options?: ErrorOptions
  ) {
    super(`could not load template for id ${idEmailTemplate}`, options);
    this.name = 'EmailMakerLoadError';
  }
}

/** Per-save options threaded into the storage adapter, either per call via `editor.store(opts)` or as session defaults via `setEmailMakerStoreOpts`. */
export interface EmailMakerStoreOpts {
  saveActionType?: SaveActionType;
  isScheduled?: boolean;
  scheduledTime?: string | null;
}

/** Request body of emailmaker/asyncSaveEmailTemplate. */
interface SaveEmailTemplateRequest {
  templateContent: Record<string, string>;
  mode: string;
  idEmailTemplate: string | null;
  isInHouseDesigner: string | null;
  name: string;
  htmlContent: string;
  designIDs: string;
  mediaQueries: string;
  hasGifContent: boolean;
  hasPremContent: boolean;
  imageHashedFilenames: string;
  videoHashedFilenames: string;
  isScheduled: boolean;
  scheduledTime: string | null;
  /** Gates the backend's structured-data augmentation (e.g. invalid-campaign-urls invalidUrls) to the React port. */
  isReactEmailMaker: true;
  /** Omitted when unset; PHP's `if ($saveActionType)` treats absent and empty string identically. */
  saveActionType?: SaveActionType;
}

interface SaveEmailTemplateResponse {
  idEmailTemplate: string;
  idEmailCampaign: string | null;
}

interface LoadEmailTemplateResponse {
  templateContent: string;
}

interface EmailMakerStorageSessionState {
  mode: EmailMakerMode;
  idEmailTemplate: string | null;
  idEmailCampaign: string | null;
  isSaving: boolean;
  isSaveQueued: boolean;
  hasSavedOnce: boolean;
  canCurrentUserAutosave: boolean;
  hasLoadFailed: boolean;
  /**
   * True when the redis cache holds changes not yet flushed to the database. Set by every
   * successful cache save, cleared by saveEmailTemplateToDb. Gates the navigate-away flush so an
   * unchanged template doesn't re-hit saveEmailTemplateToDb on every tab switch.
   */
  needsDbFlush: boolean;
}

/** Must run before the storage adapter is used; called by `importStorage`. */
export function initEmailMakerStorageState(editor: Editor, store: EmailMakerImmutableStore): void {
  const state: EmailMakerStorageSessionState = {
    mode: store.mode,
    idEmailTemplate: store.idEmailTemplate,
    idEmailCampaign: store.idEmailMarketingCampaign,
    isSaving: false,
    isSaveQueued: false,
    hasSavedOnce: false,
    canCurrentUserAutosave: store.doSetAutosaveActive,
    hasLoadFailed: false,
    needsDbFlush: false,
  };
  editor.getModel().set(STORAGE_STATE_MODEL_KEY, state);
}

/** The current template id, owned by storage; becomes the server-assigned id after the first successful save. */
export function getCurrentIdEmailTemplate(editor: Editor): string | null {
  return findStorageState(editor)?.idEmailTemplate ?? null;
}

/** Replaces the legacy window.PMW.idEmailCampaign global. */
export function getCurrentIdEmailCampaign(editor: Editor): string | null {
  return findStorageState(editor)?.idEmailCampaign ?? null;
}

/** Uses the storage-owned campaign id when present, otherwise saves once to mint an id, then fetches the full campaign by id. */
export async function resolveEmailCampaign(editor: Editor): Promise<EmailCampaign | undefined> {
  let campaignId = getCurrentIdEmailCampaign(editor);

  if (!campaignId) {
    await saveEmailTemplateToCache(editor);
    campaignId = getCurrentIdEmailCampaign(editor);
  }

  if (!campaignId) {
    return undefined;
  }

  return getEmailMarketingCampaignById(campaignId);
}

/** Current mode — transitions START/COPY → LOAD after the first successful save. */
export function getCurrentMode(editor: Editor): EmailMakerMode {
  return findStorageState(editor)?.mode ?? EmailMakerMode.START;
}

/** True after the first successful cache save; gates the navigate-away DB flush and the first-save GA event. */
export function hasEmailTemplateSavedOnce(editor: Editor): boolean {
  return findStorageState(editor)?.hasSavedOnce ?? false;
}

/** Whether the current user may autosave this template (seeded from doSetAutosaveActive; becomes true after the first save of a new/copied template). */
export function canCurrentUserAutosaveTemplate(editor: Editor): boolean {
  return findStorageState(editor)?.canCurrentUserAutosave ?? false;
}

/** True while a save is on the wire or queued; the save-state hook keeps showing SAVING until this clears. */
export function isEmailMakerSaveInFlight(editor: Editor): boolean {
  const state = findStorageState(editor);
  return state ? state.isSaving || state.isSaveQueued : false;
}

/** True when GrapesJS autosave is live for this session (setEmailMakerAutosaveEnabled(true) called and not suppressed by Beacon-API or load-failed guards). */
export function isEmailMakerAutosaveEnabled(editor: Editor): boolean {
  return editor.StorageManager.isAutosave();
}

/**
 * True once the template fetch has failed. Autosave must stay off while this is set — the first
 * store would overwrite the saved template with the empty canvas.
 */
export function hasEmailTemplateLoadFailed(editor: Editor): boolean {
  return findStorageState(editor)?.hasLoadFailed ?? false;
}

/**
 * The storage adapter's `store` body.
 *
 * Queuing: a save requested while one is in-flight marks the queue flag and resolves immediately.
 * After a successful save, one queued save is replayed with fresh project data. After a failure
 * the queue flag is kept but nothing is replayed until the next success.
 * On failure: autosave is disabled; a typed EmailMakerSaveError/EmailMakerConcurrentEditError is
 * thrown and forwarded through `storage:error:store` for the React layer.
 */
export async function storeEmailTemplate(editor: Editor, projectData: ProjectData, opts: EmailMakerStoreOpts, sources: EmailMakerSavePayloadSources): Promise<ProjectData> {
  const state = getStorageState(editor);
  if (state.isSaving) {
    state.isSaveQueued = true;
    return projectData;
  }
  state.isSaving = true;

  const requestMode = state.mode;
  const request = buildSaveEmailTemplateRequest(editor, projectData, opts, sources, state);

  try {
    const response = await postSaveEmailTemplate(request);
    applySaveSuccess(editor, state, requestMode, response);
    state.isSaving = false;
    scheduleQueuedSaveReplay(editor, state);
    return projectData;
  } catch (e: unknown) {
    setEmailMakerAutosaveEnabled(editor, false);
    await storeColorPaletteUsageOnSaveFailure(editor, state, sources);
    state.isSaving = false;
    throw e;
  }
}

/**
 * The storage adapter's `load` body. Returns parsed templateContent whose per-module values are
 * still JSON strings — GrapesJS's loadProjectData JSON-parses string module payloads itself.
 * Failures throw EmailMakerLoadError through `storage:error:load` for the React layer.
 */
export async function loadEmailTemplateContent(editor: Editor, idEmailTemplate: string): Promise<ProjectData> {
  const versionParam = new URLSearchParams(window.location.search).get('version');

  try {
    const data = (await window.PMW.writeLocal(LOAD_EMAIL_TEMPLATE_ENDPOINT, {idEmailTemplate, ...(versionParam ? {version: versionParam} : {})})) as LoadEmailTemplateResponse;
    const parsedUnknown: unknown = JSON.parse(data.templateContent);
    if (!parsedUnknown || typeof parsedUnknown !== 'object' || Array.isArray(parsedUnknown)) {
      throw new Error('Loaded template content is not a valid project data object');
    }
    // parsedUnknown is a non-null, non-array object. ProjectData is {[key:string]: any} which
    // object satisfies structurally; TS infers the return type correctly without a cast.
    return parsedUnknown;
  } catch (e: unknown) {
    // Set BEFORE the rethrow: GrapesJS catches the load failure and still fires `load`, so the load sequence needs the flag (see hasEmailTemplateLoadFailed).
    getStorageState(editor).hasLoadFailed = true;
    throw new EmailMakerLoadError(idEmailTemplate, {cause: e});
  }
}

/**
 * Triggers a full save through the storage manager; resolves with the stored project data or null
 * on failure. When a save is already in flight, only queues it and resolves immediately — a
 * follow-up DB flush can race the queued save.
 */
export async function saveEmailTemplateToCache(editor: Editor, opts: EmailMakerStoreOpts = {}): Promise<ProjectData | null> {
  try {
    return await editor.store(opts);
  } catch (e: unknown) {
    logEmailMakerError('saveEmailTemplateToCache: save failed', e);
    return null;
  }
}

/**
 * Asks the backend to flush the redis-cached payload to the database. Used on navigate-away:
 * sendBeacon when autosave is on, otherwise window.PMW.write.
 */
export async function saveEmailTemplateToDb(editor: Editor): Promise<void> {
  const state = getStorageState(editor);
  if (!state.idEmailTemplate || !state.hasSavedOnce || !state.needsDbFlush) {
    return;
  }

  const url = window.PMW.util.site_url(SAVE_EMAIL_TEMPLATE_TO_DB_ENDPOINT);
  const body = JSON.stringify({idEmailTemplate: state.idEmailTemplate});

  // Cleared optimistically: the cache and DB are in sync once the flush is dispatched, so a
  // subsequent navigate-away with no further edits must not re-hit the endpoint.
  state.needsDbFlush = false;
  if (isBeaconApiAvailable() && editor.StorageManager.isAutosave()) {
    navigator.sendBeacon(url, body);
  } else {
    try {
      await window.PMW.write(url, body);
    } catch (e: unknown) {
      // Restore the flag so the next navigate-away retries the flush.
      state.needsDbFlush = true;
      logEmailMakerError('saveEmailTemplateToDb: DB flush failed', e);
      throw e;
    }
  }
}

/** Full save, then DB flush for logged-in users. */
export async function saveEmailTemplateToDbAndCache(editor: Editor): Promise<void> {
  await saveEmailTemplateToCache(editor);
  if (isUserLoggedIn()) {
    await saveEmailTemplateToDb(editor);
  }
}

/** Fire-and-forget preview regeneration for the current template. */
export function generateEmailTemplatePreview(editor: Editor): void {
  const idEmailTemplate = getCurrentIdEmailTemplate(editor);
  if (idEmailTemplate) {
    void window.PMW.write(window.PMW.util.site_url(`${GENERATE_EMAIL_TEMPLATE_PREVIEW_ENDPOINT}/${idEmailTemplate}`), {});
  }
}

/**
 * Sets autosave; guarded: stays off when the Beacon API is missing (navigate-away flush depends on
 * sendBeacon) and can never be re-enabled after a failed load (autosaving empty canvas would
 * overwrite the saved template).
 */
export function setEmailMakerAutosaveEnabled(editor: Editor, enabled: boolean): void {
  editor.StorageManager.setAutosave(enabled && !hasEmailTemplateLoadFailed(editor) && isBeaconApiAvailable());
}

function findStorageState(editor: Editor): EmailMakerStorageSessionState | undefined {
  return editor.getModel().get(STORAGE_STATE_MODEL_KEY) as EmailMakerStorageSessionState | undefined;
}

function getStorageState(editor: Editor): EmailMakerStorageSessionState {
  const state = findStorageState(editor);
  if (!state) {
    throw new Error('Email maker storage state is not initialized. Call initEmailMakerStorageState first.');
  }
  return state;
}

function buildSaveEmailTemplateRequest(
  editor: Editor,
  projectData: ProjectData,
  opts: EmailMakerStoreOpts,
  sources: EmailMakerSavePayloadSources,
  state: EmailMakerStorageSessionState
): SaveEmailTemplateRequest {
  const request: SaveEmailTemplateRequest = {
    templateContent: buildTemplateContentForSave(projectData),
    mode: state.mode,
    idEmailTemplate: state.idEmailTemplate,
    isInHouseDesigner: getURISegment(IN_HOUSE_DESIGNER_URI_SEGMENT),
    name: getTemplateName(editor),
    htmlContent: sources.getInlinedHtml(editor),
    designIDs: getDesignIds(editor),
    mediaQueries: getMediaQueries(editor),
    hasGifContent: doesEmailContainGif(editor),
    hasPremContent: doesEmailContainPremiumContent(editor),
    imageHashedFilenames: getImageAssetHashedNames(editor),
    videoHashedFilenames: getVideoAssetHashedNames(editor),
    isScheduled: opts.isScheduled ?? false,
    scheduledTime: opts.scheduledTime ?? null,
    isReactEmailMaker: true,
  };
  if (opts.saveActionType !== undefined) {
    request.saveActionType = opts.saveActionType;
  }
  return request;
}

async function postSaveEmailTemplate(request: SaveEmailTemplateRequest): Promise<SaveEmailTemplateResponse> {
  try {
    return (await window.PMW.writeLocal(SAVE_EMAIL_TEMPLATE_ENDPOINT, request)) as SaveEmailTemplateResponse;
  } catch (e: unknown) {
    throw toEmailMakerSaveError(e);
  }
}

function applySaveSuccess(editor: Editor, state: EmailMakerStorageSessionState, requestMode: EmailMakerMode, response: SaveEmailTemplateResponse): void {
  if (requestMode === EmailMakerMode.START || requestMode === EmailMakerMode.COPY) {
    window.PMW.gtm.trackGA4CustomEvent(GA_EVENT_DESIGN_CREATED, {
      [GA4EventParamName.SOURCE]: requestMode === EmailMakerMode.START ? GA_SOURCE_BLANK_CANVAS : GA_SOURCE_TEMPLATE,
    });
    state.idEmailTemplate = response.idEmailTemplate;
    state.idEmailCampaign = response.idEmailCampaign ?? null;
    state.mode = EmailMakerMode.LOAD;
    state.canCurrentUserAutosave = true;
    // The first save creates the template; a refresh must load it instead of starting over.
    window.history.replaceState(null, '', window.PMW.util.site_url(`emailmaker/load/${response.idEmailTemplate}`));
  }

  if (!state.hasSavedOnce) {
    window.PMW.gtm.trackGA4CustomEvent(GA_EVENT_DESIGN_SAVED);
    state.hasSavedOnce = true;
  }

  if (!state.idEmailCampaign && response.idEmailCampaign) {
    state.idEmailCampaign = response.idEmailCampaign;
  }

  // A successful cache write means redis now holds data the DB doesn't; arm the navigate-away flush.
  state.needsDbFlush = true;
  setEmailMakerAutosaveEnabled(editor, true);
}

/**
 * Replays one queued save after the current store transaction settles so the replay starts a
 * clean SAVING → SAVED cycle. The timeout re-checks that the editor still holds this exact
 * session-state object; if the editor was destroyed in the gap the replay is skipped — calling
 * editor.store() on a dead editor could serialize an empty project over the cached template.
 */
function scheduleQueuedSaveReplay(editor: Editor, state: EmailMakerStorageSessionState): void {
  if (!state.isSaveQueued) {
    return;
  }
  window.setTimeout(() => {
    if (findStorageState(editor) !== state || !state.isSaveQueued || state.isSaving) {
      return;
    }
    state.isSaveQueued = false;
    void saveEmailTemplateToCache(editor);
  }, 0);
}

/** Writes the color palette usage on the save-failure path; a palette-write failure propagates instead of the save error. */
async function storeColorPaletteUsageOnSaveFailure(editor: Editor, state: EmailMakerStorageSessionState, sources: EmailMakerSavePayloadSources): Promise<void> {
  const colorPaletteData = sources.extractColorPaletteDataToStore?.(editor) ?? null;
  if (!colorPaletteData) {
    return;
  }

  const {id, customizedFromId, customizedFromType} = colorPaletteData;
  const shouldStore = id !== undefined || (customizedFromId !== undefined && customizedFromType !== undefined);
  if (!shouldStore) {
    return;
  }

  await window.PMW.writeLocal(SAVE_COLOR_PALETTE_USAGE_ENDPOINT, {...colorPaletteData, idEmailTemplate: state.idEmailTemplate});
}

/** writeLocal rejects with an Error carrying the backend envelope's `code`/`data`. */
function toEmailMakerSaveError(e: unknown): EmailMakerSaveError {
  const rawCode = readErrorCode(e);
  const code = rawCode !== null && isKnownSaveErrorCode(rawCode) ? rawCode : null;
  const message = e instanceof Error ? e.message : '';

  if (code === EmailMakerSaveErrorCode.EXISTING_SAVE_DATA) {
    return new EmailMakerConcurrentEditError(toConcurrentEditPayload(readErrorData(e)));
  }

  if (code === EmailMakerSaveErrorCode.INVALID_CAMPAIGN_URLS) {
    return new EmailMakerSaveError(code, message, toInvalidUrls(readErrorData(e)));
  }

  return new EmailMakerSaveError(code, message);
}

function isKnownSaveErrorCode(code: string): code is EmailMakerSaveErrorCode {
  return Object.values<string>(EmailMakerSaveErrorCode).includes(code);
}

function readErrorCode(e: unknown): string | null {
  if (e && typeof e === 'object' && 'code' in e) {
    const value = (e as Record<string, unknown>).code;
    return typeof value === 'string' ? value : null;
  }
  return null;
}

function readErrorData(e: unknown): Record<string, unknown> {
  if (e && typeof e === 'object' && 'data' in e) {
    const data = (e as Record<string, unknown>).data;
    if (data && typeof data === 'object') {
      return data as Record<string, unknown>;
    }
  }
  return {};
}

/** Accepts only an array of strings — `invalidUrls` is an unknown boundary value; returns undefined otherwise. */
function toInvalidUrls(data: Record<string, unknown>): string[] | undefined {
  const value = data.invalidUrls;
  if (Array.isArray(value) && value.every((url): url is string => typeof url === 'string')) {
    return value;
  }
  return undefined;
}

function toConcurrentEditPayload(data: Record<string, unknown>): ConcurrentEditPayload {
  return {
    lastModified: typeof data.lastModified === 'string' ? data.lastModified : null,
    currentUserPictureURL: typeof data.currentUserPictureURL === 'string' ? data.currentUserPictureURL : '',
    otherUserPictureURL: typeof data.otherUserPictureURL === 'string' ? data.otherUserPictureURL : '',
    otherUserName: typeof data.otherUserName === 'string' ? data.otherUserName : '',
  };
}

function isBeaconApiAvailable(): boolean {
  return typeof navigator.sendBeacon === 'function';
}
